/* eslint-disable */
import { makeAutoObservable, observable, runInAction, toJS } from 'mobx';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import { PAYROLL_STATUS, SETTLED_PAYROLL_STATUSES } from '../constants';

import errors from './errors';

import collectiveApi from 'modules/common/collectiveApi';
import { tryToCatchApiCall } from '../queries/tryToCatchApiCall';
import { PAYROLL_ANALYTICS, trackPayrollAction } from 'modules/Payroll/constants/trackPayrollAction';

import { Payroll } from 'modules/HubV2/Bookkeeping/services/usePayrollJournalReport/usePayrollJournalReport.interface';
import { GustoCompanyData } from 'services/gusto/queries/useGustoCompany/useGustoCompany.interface';
import { GustoEmployeeData } from 'services/gusto/queries/useGustoCompanyEmployees/useGustoCompanyEmployees.interface';
import { Salary } from 'modules/Payroll/Home/Compensation/interface';

/** The fields of Gusto's pay schedule this store's readers actually use. */
interface GustoPaySchedule {
    uuid: string;
    version: string;
    auto_payroll: boolean;
    frequency: string;
}

/** Gusto's embedded onboarding status. */
interface GustoCompanyOnboardingStatus {
    onboarding_completed: boolean;
    onboarding_steps: unknown[];
}

/** What Gusto needs to open an off-cycle payroll — not a whole `Payroll`, which is the response. */
interface OffCyclePayrollPayload {
    start_date: string;
    end_date: string;
    check_date: string;
    off_cycle_reason: string;
}

/** The body of a failed Gusto call: `{errors: [{message}]}` on a validation failure, else `{error}`. */
interface GustoErrorData {
    errors?: { message?: string }[];
    error?: string;
}

/**
 * The response body of a rejected call, whatever shape the rejection itself has.
 *
 * Deliberately structural rather than `axios.isAxiosError`: callers and tests both reject with
 * plain objects carrying a `response`, and the brand check would drop their bodies on the floor
 * and report the generic message instead.
 */
function responseDataOf(error: unknown): GustoErrorData | undefined {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return undefined;
    }

    return (error as { response?: { data?: GustoErrorData } }).response?.data;
}

/**
 * A compensation once this store has stamped the employee's name onto it. Gusto does not send
 * names on a payroll's compensations — `setPayrollCompensationEmployeeNames` looks them up from
 * `employees` and attaches them — so they are typed here rather than on the response interface.
 */
type NamedEmployeeCompensation = Payroll['employee_compensations'][number] & {
    first_name?: string;
    last_name?: string;
};

export default class GustoStore {
    company: GustoCompanyData | null = null;

    payrollsById = observable.map<string, Payroll>();

    /**
     * The latest write claimed per payroll, so a reply that was asked for first but arrives last
     * cannot overwrite a newer one. Deliberately not observable: it orders writes, nothing renders
     * from it, and making it observable would re-render every reader on each request.
     */
    private payrollWriteSeq = new Map<string, number>();

    employees: GustoEmployeeData[] = [];

    payrollTaxes: Record<string, unknown> = {};

    salary: Partial<Salary> = {};

    paySchedule: Partial<GustoPaySchedule> = {};

    /** Several active schedules and none marked default: `paySchedule` is shown, not acted on. */
    isPayScheduleAmbiguous = false;

    companyOnboardingStatus: Partial<GustoCompanyOnboardingStatus> = {};

    constructor() {
        // The explicit key is mobx's escape hatch for annotating a private field.
        makeAutoObservable<GustoStore, 'payrollWriteSeq'>(this, { payrollWriteSeq: false });
    }

    /**
     * Claims the next write for a payroll and reports, later, whether that claim still stands.
     *
     * A read claims when it is *issued*, so "older" means "asked for earlier" — the whole point.
     * A write that the server has already accepted claims when it *lands* instead: an edit, submit,
     * cancel or skip is newer than anything a read in flight can know, and claiming it on entry
     * would let a read issued during the call write pre-action data over the response.
     */
    private claimPayrollWrite(payrollId: string) {
        const claim = (this.payrollWriteSeq.get(payrollId) ?? 0) + 1;
        this.payrollWriteSeq.set(payrollId, claim);

        return () => this.payrollWriteSeq.get(payrollId) === claim;
    }

    migrateCompany = async () => {
        await this.fetchCompany();
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            throw new Error('Cannot migrate: no Gusto company is loaded.');
        }

        try {
            await collectiveApi.put(`gusto/companies/${companyUuid}/migrate/`);
        } catch (error) {
            sendDatadogRumError('Error migrating company', error, { source: 'GustoStore' });
            throw error;
        }
    };

    /**
     * @returns {Object[]} array of payroll objects of `payroll_type` === `Regular`
     */
    get payrolls() {
        return this.allPayrolls?.filter((payroll) => payroll.payroll_type === 'Regular') || [];
    }

    /**
     * @returns {Object[]} array of payroll objects of all `payroll_type`
     */
    get allPayrolls() {
        return [...toJS(this.payrollsById).values()].sort((prev, next) =>
            Date.parse(prev.check_date) > Date.parse(next.check_date) ? -1 : 1
        );
    }

    get nextPayroll() {
        // Get all regular payrolls sorted by check_date (oldest first)
        const sortedPayrolls = [...this.payrolls].sort((prev, next) =>
            Date.parse(prev.check_date) < Date.parse(next.check_date) ? -1 : 1
        );

        // First priority: Find processed but not yet paid payrolls, i.e. still in flight
        const processedUnpaidPayroll = sortedPayrolls.find(
            (payroll) =>
                payroll.processed &&
                payroll.status !== PAYROLL_STATUS.paid &&
                !SETTLED_PAYROLL_STATUSES.includes(payroll.status)
        );

        if (processedUnpaidPayroll) {
            return processedUnpaidPayroll;
        }

        // Second priority: Find the oldest unprocessed payroll, skipping any voided/cancelled/skipped directly in Gusto
        const oldestUnprocessedPayroll = sortedPayrolls.find(
            (payroll) => !payroll.processed && !SETTLED_PAYROLL_STATUSES.includes(payroll.status)
        );

        if (oldestUnprocessedPayroll) {
            return oldestUnprocessedPayroll;
        }

        // Third priority: If all payrolls are processed and paid, return the last paid payroll
        const paidPayrolls = sortedPayrolls.filter(
            (payroll) => payroll.processed && payroll.status === PAYROLL_STATUS.paid
        );

        return paidPayrolls[paidPayrolls.length - 1]; // Last (most recent) paid payroll
    }

    get previousPayroll() {
        // last paid/pending payroll, including Regular and off-cycle ones
        let { allPayrolls } = this;
        if (allPayrolls.length <= 1) {
            return;
        }

        if (allPayrolls[0].payroll_type === 'Regular') {
            allPayrolls = allPayrolls.slice(1);
        }
        return find(allPayrolls, (payroll) => {
            return ([PAYROLL_STATUS.paid, PAYROLL_STATUS.pending_payment] as string[]).includes(payroll.status);
        });
    }

    get isCompanyActive() {
        return this.company?.company_status === 'Approved' && !this.company?.is_suspended;
    }

    fetchCompany = async () => {
        if (!isEmpty(this.company)) {
            return;
        }

        await tryToCatchApiCall(async () => {
            const { data: company } = await collectiveApi.get('gusto/companies/');
            runInAction(() => (this.company = company || {}));
        }, 'getting a company');
    };

    async fetchAllPayrolls() {
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            return;
        }

        await tryToCatchApiCall(async () => {
            await this.fetchEmployees();
            const { data: payrolls } = await collectiveApi.get(`gusto/companies/${companyUuid}/payrolls/`);
            runInAction(() => {
                payrolls.forEach((payroll: Payroll) => {
                    this.payrollsById.set(payroll.payroll_uuid, this.setPayrollCompensationEmployeeNames(payroll));
                });
            });
        }, 'getting payrolls for a company');
    }

    async fetchPayrollTaxes(payroll_uuid: string) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid || !payroll_uuid) {
            return;
        }

        try {
            const { data: taxes } = await collectiveApi.get(
                `gusto/companies/${companyUuid}/payrolls/${payroll_uuid}/tax-report/`
            );

            runInAction(() => (this.payrollTaxes[payroll_uuid] = taxes));
        } catch (error) {
            sendDatadogRumError('Error fetching payroll taxes', error, { source: 'GustoStore' });
        }
    }

    async fetchPayroll(payrollId: string, showCalculation = 'true') {
        // showCalculation = "true" to get all details such as calculated tax, benifits
        const companyUuid = this.company?.uuid;

        if (!companyUuid || !payrollId) {
            return;
        }

        const isLatestRequest = this.claimPayrollWrite(payrollId);

        return tryToCatchApiCall(async () => {
            await this.fetchEmployees();
            const { data: payroll } = await collectiveApi.get(
                `gusto/companies/${companyUuid}/payrolls/${payrollId}/${showCalculation}/`
            );

            // Superseded responses are still returned to the caller that asked for them — they are
            // only barred from the store. Nothing here rejects, so a superseded poll is not
            // reported to the member and does not spend its consecutive-failure budget.
            if (isLatestRequest()) {
                runInAction(() => {
                    this.payrollsById.set(payroll.payroll_uuid, this.setPayrollCompensationEmployeeNames(payroll));
                });
            }

            return payroll;
        }, 'getting a payroll');
    }

    async editPayroll(payrollId: string, payload: Record<string, unknown>) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            throw new Error('Cannot edit payroll: no Gusto company is loaded.');
        }

        await tryToCatchApiCall(async () => {
            const { data: payroll } = await collectiveApi.patch(
                `gusto/companies/${companyUuid}/payrolls/${payrollId}/edit/`,
                payload
            );

            this.claimPayrollWrite(payrollId);
            runInAction(() => {
                this.payrollsById.set(payrollId, payroll);
            });
        }, 'editing a payroll');
    }

    async fetchEmployees() {
        const companyUuid = this.company?.uuid;

        if (!companyUuid || this.employees.length) {
            return;
        }

        await tryToCatchApiCall(async () => {
            const { data: employees } = await collectiveApi.get(`gusto/companies/${companyUuid}/employees/`);

            runInAction(() => {
                this.employees = employees;
            });
        }, 'getting employees');
    }

    async submitPayroll(currentPayroll: Payroll) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            return;
        }

        try {
            const payroll = await tryToCatchApiCall(async () => {
                const { data } = await collectiveApi.put(
                    `gusto/companies/${companyUuid}/payrolls/${currentPayroll.payroll_uuid}/submit/`
                );
                this.claimPayrollWrite(data.payroll_uuid);
                runInAction(() => {
                    this.payrollsById.set(data.payroll_uuid, this.setPayrollCompensationEmployeeNames(data));
                });
                return data;
            }, 'submitting a payroll');
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_SUBMITTED,
                action: 'submit_payroll',
                outcome: 'accepted',
                source: 'gusto_store',
            });
            return payroll;
        } catch (error) {
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_SUBMITTED,
                action: 'submit_payroll',
                outcome: 'failed',
                source: 'gusto_store',
            });
            throw error;
        }
    }

    async cancelPayroll(payroll: Payroll) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid || !payroll?.payroll_uuid) {
            return;
        }

        try {
            const result = await tryToCatchApiCall(async () => {
                const { data } = await collectiveApi.put(
                    `gusto/companies/${companyUuid}/payrolls/${payroll.payroll_uuid}/cancel/`
                );
                this.claimPayrollWrite(data.payroll_uuid);
                runInAction(() => {
                    this.payrollsById.set(data.payroll_uuid, data);
                });
                return data;
            }, 'cancelling a payroll');
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_CANCELLED,
                action: 'cancel_payroll',
                outcome: 'accepted',
                source: 'gusto_store',
            });
            return result;
        } catch (error) {
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_CANCELLED,
                action: 'cancel_payroll',
                outcome: 'failed',
                source: 'gusto_store',
            });
            throw error;
        }
    }

    async skipPayroll(payroll: Payroll) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid || !payroll?.payroll_uuid) {
            return;
        }

        try {
            const result = await tryToCatchApiCall(async () => {
                // The skip endpoint answers with an empty body, unlike submit and cancel, so the run is
                // named by the payroll we were handed. It is marked skipped here, so the card stops
                // offering Skip at once; the caller's refresh then replaces it with the provider's record.
                await collectiveApi.put(`gusto/companies/${companyUuid}/payrolls/${payroll.payroll_uuid}/skip/`);
                this.claimPayrollWrite(payroll.payroll_uuid);
                runInAction(() => {
                    this.payrollsById.set(payroll.payroll_uuid, {
                        ...payroll,
                        processed: true,
                        status: PAYROLL_STATUS.skipped,
                    });
                });

                return payroll;
            }, 'skipping a payroll');
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_SKIPPED,
                action: 'skip_payroll',
                outcome: 'accepted',
                source: 'gusto_store',
            });
            return result;
        } catch (error) {
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.RUN_SKIPPED,
                action: 'skip_payroll',
                outcome: 'failed',
                source: 'gusto_store',
            });
            throw error;
        }
    }

    async createOffCyclePayroll(payload: OffCyclePayrollPayload, analytics: Record<string, unknown> = {}) {
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            return;
        }

        try {
            const { data: result } = await collectiveApi.post(`gusto/companies/${companyUuid}/payrolls/`, payload);
            runInAction(() => {
                this.payrollsById.set(result.payroll_uuid, result);
            });
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.OFF_CYCLE_CREATED,
                action: 'create_off_cycle_payroll',
                outcome: 'accepted',
                source: 'gusto_store',
                properties: analytics,
            });
            return result;
        } catch (error) {
            sendDatadogRumError('Error creating off-cycle payroll', error, { source: 'GustoStore' });
            trackPayrollAction({
                name: PAYROLL_ANALYTICS.OFF_CYCLE_CREATED,
                action: 'create_off_cycle_payroll',
                outcome: 'failed',
                source: 'gusto_store',
                properties: analytics,
            });
            let errorMessage = errors.CREATE_OFF_CYCLE_PAYROLL;
            // Gusto validation failures come back as {errors: [{message}]}; anything else uses {error}
            const data = responseDataOf(error);
            errorMessage = data?.errors?.[0]?.message || data?.error || errorMessage;
            return { error: errorMessage };
        }
    }

    async fetchCompanyPayrollSetupUrl(memberId?: string) {
        try {
            const { data } = await collectiveApi.get(
                `gusto/embedded/company/${memberId ? `?member_id=${memberId}` : ''}`
            );
            return data.url;
        } catch (error) {
            sendDatadogRumError('Error fetching company payroll setup URL', error, { source: 'GustoStore' });
            // A rejection with no response body is rethrown as-is: reading `.data` off it turned a
            // network failure into "cannot read property 'data' of undefined".
            throw responseDataOf(error) ?? error;
        }
    }

    async fetchEmployeePayrollSetupUrl(memberId?: string) {
        try {
            const { data } = await collectiveApi.get(
                `gusto/embedded/employee/${memberId ? `?member_id=${memberId}` : ''}`
            );
            return data.url;
        } catch (error) {
            sendDatadogRumError('Error fetching employee payroll setup URL', error, { source: 'GustoStore' });
            // A rejection with no response body is rethrown as-is: reading `.data` off it turned a
            // network failure into "cannot read property 'data' of undefined".
            throw responseDataOf(error) ?? error;
        }
    }

    async fetchCompanyOnboardingStatus(memberId?: string) {
        try {
            const { data } = await collectiveApi.get(
                `gusto/embedded/company-onboarding-status/${memberId ? `?member_id=${memberId}` : ''}`
            );

            runInAction(() => (this.companyOnboardingStatus = data));
            return data;
        } catch (error) {
            sendDatadogRumError('Error fetching company onboarding status', error, { source: 'GustoStore' });
            throw new Error('An error occurred while getting embedded onboarding status');
        }
    }

    /**
     *
     * @returns {Object[]} returns all `Processed` payrolls, including regular and off-cycle
     */
    getProcessedPayrolls() {
        return this.allPayrolls.filter((payroll) => payroll.processed);
    }

    refreshPayrollAndTaxes(payrollId: string) {
        this.fetchPayroll(payrollId);
        this.fetchPayrollTaxes(payrollId);
    }

    async setPayScheduleAutopilot(pay_schedule_uuid: string, version: string, auto_payroll: boolean) {
        if (!pay_schedule_uuid) {
            return;
        }

        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            throw new Error('Cannot update the pay schedule: no Gusto company is loaded.');
        }

        const response = await collectiveApi.put(
            `/gusto/companies/${companyUuid}/pay-schedules/${pay_schedule_uuid}/`,
            { version, auto_payroll }
        );

        runInAction(() => {
            if (response.status === 200) {
                this.paySchedule = response.data;
            }
        });
        return response;
    }

    getCompanyPaySchedule = async () => {
        if (!isEmpty(this.paySchedule)) {
            return;
        }

        await this.fetchCompany();
        const companyUuid = this.company?.uuid;

        if (!companyUuid) {
            return;
        }

        await tryToCatchApiCall(async () => {
            const { data: paySchedules } = await collectiveApi.get(`/gusto/companies/${companyUuid}/pay-schedules/`);

            if (isEmpty(paySchedules)) {
                // `companyUuid` is enough to identify the member here — RUM already
                // carries the session's user, so this doesn't repeat it.
                sendDatadogRumError('No pay schedules found', new Error('No pay schedules found'), {
                    source: 'GustoStore',
                    companyUuid,
                });
            }

            runInAction(() => {
                // The provider's default schedule, as the backend marks it. The home toggles
                // auto-payroll on this one, so with several and none marked, one is shown but not toggled.
                const marked = paySchedules?.find((schedule: { is_default?: boolean }) => schedule.is_default);
                this.paySchedule = marked ?? paySchedules?.[0];
                this.isPayScheduleAmbiguous = !marked && (paySchedules?.length ?? 0) > 1;
            });
        }, 'getting company pay schedule');
    };

    setPayrollCompensationEmployeeNames(payroll: Payroll) {
        payroll.employee_compensations.forEach((employee_compensation: NamedEmployeeCompensation) => {
            const employee = this.employees.find((employee) => employee.uuid === employee_compensation.employee_uuid);

            // An unmatched compensation is left unnamed rather than throwing: the payroll is still
            // worth rendering, and `employees` can legitimately lag a freshly added worker.
            if (!employee) {
                return;
            }

            employee_compensation.first_name = employee.first_name;
            employee_compensation.last_name = employee.last_name;
        });
        return payroll;
    }

    getMemberSalary = async () => {
        if (!this.company?.uuid) {
            return;
        }

        await tryToCatchApiCall(async () => {
            const { data: salary } = await collectiveApi.get(`gusto/salary/member/`);
            runInAction(() => (this.salary = salary));
        }, 'getting salary');
    };

    putMemberSalary = async (payload: Record<string, unknown>) => {
        return collectiveApi.put(`gusto/salary/member/`, { salary: payload });
    };
}
